/**
 * ForecastTable — matrix "III. CASH FLOWS FORECAST" (PBI pivotTable layout
 * Tabular, KHÔNG subtotal): cột [label | unit | woy...], dòng theo đúng thứ tự
 * template view (BE đã sort). Khác PnLTable ở styling THEO DÒNG do template
 * quy định (bold/italic/red từ `lv1_format`) + label giữ leading spaces thụt
 * cấp → component riêng, tái dùng tokens + fmtNum của dashboard PnL.
 */
import { Box, Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material'

import { fmtNum } from '../pnl/format'
import { PNL_COLORS } from '../pnl/tokens'
import type { PnLRow } from '../pnl/PnLTable'

const HEAD_SX = {
  bgcolor: PNL_COLORS.headerBg,
  backgroundImage: PNL_COLORS.headerGradient,
  color: PNL_COLORS.headerText,
  fontWeight: 900,
  fontSize: { xs: '0.56rem', xl: '0.6rem' },
  lineHeight: 1.15,
  py: 0.5,
  px: { xs: 0.5, xl: 0.75 },
  border: `1px solid ${PNL_COLORS.border}`,
  borderBottom: '2px solid rgba(11, 54, 51, 0.34)',
  whiteSpace: 'nowrap',
} as const

const CELL_SX = {
  fontSize: { xs: '0.58rem', xl: '0.62rem' },
  lineHeight: 1.25,
  py: 0.35,
  px: { xs: 0.5, xl: 0.75 },
  verticalAlign: 'top',
} as const

interface ForecastTableProps {
  /** Nhãn cột tuần (woy, vd "2026-24") — key giá trị tương ứng `w<index>`. */
  weeks: string[]
  rows: PnLRow[]
}

export default function ForecastTable({ weeks, rows }: ForecastTableProps) {
  return (
    <Box sx={{ overflow: 'auto', border: `1px solid ${PNL_COLORS.border}`, borderRadius: 1 }}>
      <Table size="small" sx={{ borderCollapse: 'collapse', '& td': { border: `1px solid ${PNL_COLORS.border}` } }}>
        <TableHead>
          <TableRow>
            {/* 2 cột row-header (PBI để trống label). */}
            <TableCell sx={{ ...HEAD_SX, minWidth: 250 }} />
            <TableCell sx={HEAD_SX} />
            {weeks.map((w) => (
              <TableCell key={w} align="right" sx={HEAD_SX}>
                {w}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, ri) => {
            const bgcolor = ri % 2 === 1 ? PNL_COLORS.zebraBg : PNL_COLORS.rowBg
            // Style label theo template (lv1_format PBI): đỏ đậm cho các dòng
            // Surplus/(Deficit), đậm cho title section, nghiêng cho line item.
            const labelSx = {
              ...CELL_SX,
              whiteSpace: 'pre' as const,
              fontWeight: row.bold ? 700 : 400,
              fontStyle: row.italic ? 'italic' : 'normal',
              color: row.red ? PNL_COLORS.negative : PNL_COLORS.positive,
              bgcolor,
            }
            return (
              <TableRow key={ri}>
                <TableCell sx={labelSx}>{row.label == null ? '' : String(row.label)}</TableCell>
                <TableCell sx={{ ...labelSx, whiteSpace: 'nowrap' }}>
                  {row.unit == null ? '' : String(row.unit)}
                </TableCell>
                {weeks.map((w, wi) => {
                  const raw = row[`w${wi}`]
                  const r =
                    raw == null || raw === ''
                      ? { text: '', neg: false }
                      : fmtNum(Number(raw))
                  return (
                    <TableCell
                      key={w}
                      align="right"
                      sx={{
                        ...CELL_SX,
                        whiteSpace: 'nowrap',
                        // PBI values config: italic, không đậm.
                        fontStyle: 'italic',
                        color: r.neg ? PNL_COLORS.negative : PNL_COLORS.positive,
                        bgcolor,
                      }}
                    >
                      {r.text}
                    </TableCell>
                  )
                })}
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
    </Box>
  )
}
